import React, { useState } from 'react';
import { useApi } from '@axiom/ui';
import type { Candidate, Experience } from '@axiom/validation';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateExperiencesApi } from '../../api/candidate-experiences-api';
import { CandidateIndustriesApi } from '../../api/candidate-industries-api';
import { CandidateExperienceForm } from '../CandidateExperienceForm/CandidateExperienceForm';

import { CandidateExperienceIndustrySummary } from './CandidateExperienceIndustrySummary';
import { CandidateExperienceList } from './CandidateExperienceList';
import { CandidateExperienceUtil } from './candidate-experience-util';

export const CandidateExperience = ({
  candidate,
}: {
  candidate: Candidate;
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExperience, setEditingExperience] =
    useState<Partial<Experience> | null>(null);

  const [
    { data: taxonomy },
    { data: candidateExperiences },
    { data: candidateIndustries },
  ] = useApi(
    TaxonomyApi.readTaxonomy(),
    CandidateExperiencesApi.readExperiences(candidate.id),
    CandidateIndustriesApi.readIndustries(candidate.id)
  );

  const experiences = CandidateExperienceUtil.sortExperiences(
    candidateExperiences || []
  );

  const firstExperienceWithUnapprovedExceptionRequest = [
    ...experiences.axiom,
    ...experiences.other,
  ].find(
    experience =>
      experience.exceptionRequestedAt && !experience.exceptionApprovedAt
  );

  const handleOpenForm = (experience: Partial<Experience> = {}) => {
    setEditingExperience(experience);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingExperience(null);
  };

  return (
    <div data-test="TALENT_EXPERIENCE_TAB_CONTAINER">
      {(candidateIndustries?.length ?? 0) > 0 && (
        <CandidateExperienceIndustrySummary
          candidateIndustries={candidateIndustries}
        />
      )}
      <CandidateExperienceList
        experiences={experiences.axiom}
        itemToScrollIntoView={firstExperienceWithUnapprovedExceptionRequest}
        talentName={candidate.calculatedDisplayName}
        title="Axiom Experience"
        taxonomy={taxonomy}
        onEditExperience={handleOpenForm}
      />
      <CandidateExperienceList
        experiences={experiences.other}
        itemToScrollIntoView={firstExperienceWithUnapprovedExceptionRequest}
        talentName={candidate.calculatedDisplayName}
        title="Other Experience"
        taxonomy={taxonomy}
        canAddNew
        onAddExperience={() => {
          handleOpenForm({});
        }}
        onEditExperience={handleOpenForm}
      />
      {isFormOpen && editingExperience ? (
        <CandidateExperienceForm
          candidateId={candidate.id}
          experience={editingExperience}
          onClose={handleCloseForm}
        />
      ) : null}
    </div>
  );
};
